import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

//DEPENDENCIES
import { Navigate, useLocation } from "react-router-dom";
import dayjs from "../../utils/dayjsConfig";

//UTILS
import {
  hydrateBiometricsLoggedIn,
  logout,
  removeBiometricData,
  refreshToken,
  setAuthFromUrl,
  getUserProfile,
  updateBiometricError,
} from "../../store/slices/authSlice";
import { PATH_APP } from "../../routes/paths";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import useAccountData from "../../hooks/useAccountData";
import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useSearchParams } from "react-router-dom";
import { BiometricEnrollmentError } from "../../constants";
import {
  fetchStoredBiometricCredential,
  validateBiometricCredential,
  ValidBiometricCredential,
} from "../../utils/biometricSession";
import {
  ENABLE_PROTECTED_ROUTE_LOGS,
  ENABLE_PROTECTED_ROUTE_TELEMETRY,
} from "../../constants/logging";
import { performBiometricReauth } from "../../utils/biometricReauth";
import { isOtaApplying } from "../../utils/otaState";
import LoadingSpinner from "../common/LoadingSpinner";
import loginStyles from "../../pages/LoginPage/LoginPage.module.scss";
import * as Sentry from "@sentry/react";
import {
  decideResumeAction,
  shouldBlockExpiryHandlers,
} from "./resumeAuthFlow";

interface Props {
  children: ReactNode;
}

// Activity debounce: 3 minutes
const DEBOUNCE_TIME = 3 * 60 * 1000;
const MAX_LOGOUT_TIMEOUT_MS = 24 * 60 * 60 * 1000;

const ProtectedRoute = ({ children }: Props) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const log = useCallback((...args: unknown[]) => {
    if (!ENABLE_PROTECTED_ROUTE_LOGS) return;
    // eslint-disable-next-line no-console
    console.log("[ProtectedRoute]", ...args);
  }, []);

  const { user, auth, error, biometrics } = useAppSelector(
    (state) => state.authentication,
  );
  const { isInForeground } = useAppSelector((state) => state.appState);

  // Check if there are tokens in the URL (being processed)
  const accessToken = searchParams.get("accessToken");
  const refreshTokenParam = searchParams.get("refreshToken");
  const expiresIn = searchParams.get("expiresIn");
  const expiresAt = searchParams.get("expiresAt");
  const executeAt = searchParams.get("executeAt");
  const stateParam = searchParams.get("state");

  const hasUrlTokens =
    accessToken && expiresIn && expiresAt && executeAt && refreshTokenParam;

  const [serverDiff, setServerDiff] = useState<number>();
  // Track which execute_at the current serverDiff was computed from
  const serverDiffExecuteAtRef = useRef<string | null>(null);

  // Prevent activity-based logout while resume biometric re-auth is in flight
  const resumeBiometricInProgressRef = useRef(false);
  const resumeAuthLockRef = useRef(false);

  const pathname = window?.location?.pathname;
  const isLogout = localStorage.getItem("logout");

  const executedAt = auth?.execute_at;
  const lastActivityTimeRef = useRef(dayjs().utc());
  const biometricsInProgress =
    biometrics?.inProgress || resumeBiometricInProgressRef.current;
  // Track opt-in page as an in-progress biometric state to pause expirations while user is enrolling
  const biometricsOptInPage = biometrics?.optInPage ?? null;
  const logoutTimeoutRef = useRef<number | null>(null);
  const liveTimeoutGuardsRef = useRef({
    isInForeground,
    biometricsInProgress,
    biometricsOptInPage,
    resumeAuthLock: false,
  });
  // Track latest auth data and a short grace window after biometric reauth so activity checks don't use stale tokens
  const liveAuthRef = useRef<{
    expiresAt: string | null | undefined;
    executeAt: string | null | undefined;
    serverDiff: number | undefined;
  }>({
    expiresAt: auth?.expires_at,
    executeAt: auth?.execute_at,
    serverDiff,
  });
  // Grace period end timestamp (ms) after biometric reauth to ignore stale expiry checks until new tokens settle
  const skipExpiryUntilRef = useRef(0);
  const renderGateLoggedRef = useRef<boolean | null>(null);
  const renderStateRef = useRef<string | null>(null);
  const hydrationLoggedRef = useRef(false);
  const wasInForegroundRef = useRef(isInForeground);
  const wasPausedRef = useRef(false);
  const [resumeGateActive, setResumeGateActive] = useState(false);
  const liveUserRef = useRef(user);
  const liveBiometricsLoggedInRef = useRef(biometrics?.loggedIn ?? false);
  const liveIsInForegroundRef = useRef(isInForeground);
  const liveBiometricInProgressRef = useRef(Boolean(biometrics?.inProgress));
  const liveBiometricsOptInPageRef = useRef(biometricsOptInPage);
  const resumedThisRender =
    Capacitor.isNativePlatform() &&
    isInForeground &&
    !wasInForegroundRef.current &&
    wasPausedRef.current;
  const resumeGateHold = resumeGateActive || resumedThisRender;

  const [biometricHydrationDone, setBiometricHydrationDone] = useState(
    !Capacitor.isNativePlatform(),
  );
  const { spinnerContainer } = loginStyles;

  const clearLogoutTimeout = useCallback(() => {
    if (!logoutTimeoutRef.current) return;
    window.clearTimeout(logoutTimeoutRef.current);
    logoutTimeoutRef.current = null;
  }, []);

  const syncTimeoutGuardsRef = useCallback(() => {
    liveTimeoutGuardsRef.current = {
      isInForeground: liveIsInForegroundRef.current,
      biometricsInProgress:
        liveBiometricInProgressRef.current || resumeAuthLockRef.current,
      biometricsOptInPage: liveBiometricsOptInPageRef.current,
      resumeAuthLock: resumeAuthLockRef.current,
    };
  }, []);

  const setResumeAuthLock = useCallback(
    (locked: boolean, reason: string) => {
      resumeAuthLockRef.current = locked;
      resumeBiometricInProgressRef.current = locked;
      syncTimeoutGuardsRef();
      log("resume auth lock", { locked, reason });
    },
    [log, syncTimeoutGuardsRef],
  );

  const sendResumeTelemetry = useCallback(
    (
      eventName: string,
      payload: Record<string, unknown>,
      level: "info" | "warning" | "error" = "info",
    ) => {
      if (!ENABLE_PROTECTED_ROUTE_TELEMETRY) return;
      Sentry.withScope((scope) => {
        scope.setTag("module", "ProtectedRoute");
        scope.setTag("resume_auth_event", eventName);
        scope.setTag("platform", Capacitor.getPlatform());
        scope.setTag("isNative", String(Capacitor.isNativePlatform()));
        if (liveUserRef.current?.email) {
          // Requested by product for user-level troubleshooting in Sentry
          scope.setExtra("userEmail", liveUserRef.current.email);
        }
        Object.entries(payload).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
        Sentry.captureMessage(`ProtectedRoute.${eventName}`, level);
      });
    },
    [],
  );

  const captureForcedLogout = useCallback(
    (reason: string, details: Record<string, unknown> = {}) => {
      sendResumeTelemetry(
        "forced_logout",
        {
          reason,
          ...details,
        },
        "warning",
      );
      dispatch(logout());
    },
    [dispatch, sendResumeTelemetry],
  );

  useAccountData();

  useEffect(() => {
    log("mount", {
      path: location.pathname,
      hasUrlTokens: Boolean(hasUrlTokens),
      isNative: Capacitor.isNativePlatform(),
    });
  }, [hasUrlTokens, location.pathname, log]);

  useEffect(() => {
    if (biometricsInProgress) {
      clearLogoutTimeout();
    }
  }, [biometricsInProgress, clearLogoutTimeout]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let pauseListener: { remove: () => void } | undefined;

    void (async () => {
      pauseListener = await CapApp.addListener("pause", () => {
        wasPausedRef.current = true;
        log("resume gate: pause");
      });
    })();

    return () => {
      pauseListener?.remove?.();
    };
  }, [log]);

  useLayoutEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (wasInForegroundRef.current === isInForeground) return;

    if (isInForeground) {
      if (wasPausedRef.current) {
        setResumeGateActive(true);
      }
      log("resume gate: enter", {
        origin: wasPausedRef.current ? "pause" : "inactive",
      });
      wasPausedRef.current = false;
    } else {
      setResumeGateActive(false);
      log("resume gate: background");
    }

    wasInForegroundRef.current = isInForeground;
  }, [isInForeground, log]);

  useEffect(() => {
    liveIsInForegroundRef.current = isInForeground;
    liveBiometricInProgressRef.current = Boolean(biometrics?.inProgress);
    liveBiometricsOptInPageRef.current = biometricsOptInPage;
    syncTimeoutGuardsRef();
  }, [biometrics?.inProgress, biometricsOptInPage, isInForeground, syncTimeoutGuardsRef]);

  // Keep latest auth/server diff in a ref so event handlers never see stale values
  useEffect(() => {
    liveAuthRef.current = {
      expiresAt: auth?.expires_at,
      executeAt: auth?.execute_at,
      serverDiff,
    };
  }, [auth?.expires_at, auth?.execute_at, serverDiff]);

  useEffect(() => {
    liveUserRef.current = user;
  }, [user]);

  useEffect(() => {
    liveBiometricsLoggedInRef.current = Boolean(biometrics?.loggedIn);
  }, [biometrics?.loggedIn]);

  // When biometric reauth completes and we have fresh auth, reset activity timer and add a short grace window
  useEffect(() => {
    if (!biometricsInProgress && biometrics?.loggedIn && auth?.expires_at) {
      lastActivityTimeRef.current = dayjs().utc();
      // Allow a few seconds for refreshed tokens to propagate to all handlers before enforcing expiry checks
      skipExpiryUntilRef.current = Date.now() + 5000; // 5s grace to avoid stale closure checks
    }
  }, [biometricsInProgress, biometrics?.loggedIn, auth?.expires_at]);

  // On native app resume, attempt a token refresh to keep the session alive
  useEffect(() => {
    let listener: { remove: () => void } | undefined;

    // Listener for app resume after being in background
    (async () => {
      listener = await CapApp.addListener("resume", async () => {
        if (!Capacitor.isNativePlatform()) return;
        setResumeGateActive(true);
        setResumeAuthLock(true, "resume-start");
        // Block activity/visibility handlers immediately while resume is in progress
        skipExpiryUntilRef.current = Infinity;
        try {
          if (isOtaApplying()) {
            log("resume: ota apply in progress, skipping");
            return;
          }

          // Drop any existing logout timers before kicking off biometric resume flow
          clearLogoutTimeout();
          const { expiresAt: liveExpiresAt, executeAt, serverDiff: liveServerDiff } =
            liveAuthRef.current;
          const hasAuthData = Boolean(liveExpiresAt && executeAt);
          const biometricsLoggedIn = liveBiometricsLoggedInRef.current;
          log("resume: start", {
            hasAuthData,
            biometricsLoggedIn,
          });

          const currentTime = dayjs().utc();
          const currentTimeWithDiff =
            typeof liveServerDiff === "number"
              ? currentTime.subtract(liveServerDiff, "millisecond")
              : currentTime;
          const isExpired = hasAuthData
            ? dayjs(liveExpiresAt).utc().isBefore(currentTimeWithDiff)
            : false;

          const secureSession = await fetchStoredBiometricCredential(
            "protected-route:resume",
          );
          const validation = validateBiometricCredential(secureSession);
          const storedUsername = secureSession?.username;

          // Detect keychain vs Redux desync — if the refresh token in the
          // keychain differs from Redux, the next biometric reauth will use
          // a stale token that the backend already rotated → silent logout.
          const reduxRefreshToken = auth?.refresh_token;
          const keychainRefreshToken = secureSession?.refreshToken;
          if (
            reduxRefreshToken &&
            keychainRefreshToken &&
            reduxRefreshToken !== keychainRefreshToken
          ) {
            log("resume: WARNING keychain/Redux refresh token mismatch", {
              reduxTokenPrefix: reduxRefreshToken.slice(0, 8),
              keychainTokenPrefix: keychainRefreshToken.slice(0, 8),
            });
            sendResumeTelemetry("keychain_redux_desync", {
              hasReduxToken: true,
              hasKeychainToken: true,
              tokensMatch: false,
            }, "warning");
          }

          const resumeDecision = decideResumeAction({
            hasAuthData,
            authExpired: isExpired,
            secureCredentialValid: validation.valid,
            secureCredentialExpired: validation.expired,
            biometricsLoggedIn,
          });
          sendResumeTelemetry("resume_decision", {
            resumeDecision,
            hasAuthData,
            authExpired: isExpired,
            secureCredentialValid: validation.valid,
            secureCredentialExpired: validation.expired,
            biometricsLoggedIn,
          });

          if (resumeDecision === "runBiometricReauth") {
            log("resume: attempting biometric recovery", {
              isExpired,
              hasAuthData,
              biometricsLoggedIn,
            });
            const credential = validation.credential as ValidBiometricCredential;
            const reauthResult = await performBiometricReauth({
              dispatch,
              credential,
              userEmail: storedUsername ?? liveUserRef.current?.email ?? null,
              recomputeServerDiff: true,
            });

            if (!reauthResult.success) {
              log("resume: biometric reauth failed", reauthResult);
              sendResumeTelemetry("biometric_reauth_failed", {
                step: reauthResult.step,
                error: reauthResult.error,
                errorCode: reauthResult.errorCode,
                nativeMessage: reauthResult.nativeMessage,
              });
              if (reauthResult.step === "prompt") {
                dispatch(
                  updateBiometricError(
                    reauthResult.error as BiometricEnrollmentError,
                  ),
                );
                return;
              }

              dispatch(removeBiometricData());
              captureForcedLogout("resume_biometric_reauth_failed", {
                step: reauthResult.step,
              });
              return;
            }

            if (!liveUserRef.current) {
              await dispatch(getUserProfile());
            }

            // Extend grace period so stale expiry checks don't fire before new tokens settle
            skipExpiryUntilRef.current = Date.now() + 5000;

            log("resume: biometric reauth success");
            sendResumeTelemetry("biometric_reauth_success", {
              path: "resume",
            });
            return;
          }

          if (resumeDecision === "logoutExpiredNoRecovery") {
            log("resume: logout (expired auth and no biometric recovery)", {
              isExpired,
              hasAuthData,
              secureCredentialValid: validation.valid,
            });
            if (validation.expired) {
              dispatch(removeBiometricData());
            }
            captureForcedLogout("resume_expired_without_recovery", {
              hasAuthData,
              secureCredentialValid: validation.valid,
              secureCredentialExpired: validation.expired,
            });
            return;
          }

          if (resumeDecision === "refreshToken") {
            if (liveUserRef.current) {
              log("resume: refreshing token (auth still valid)");
              dispatch(refreshToken());
            }
            return;
          }

          log("resume: end (noop)", { isExpired, hasAuthData });
        } finally {
          // Give 5s grace for new tokens to propagate before re-enabling expiry checks
          skipExpiryUntilRef.current = Date.now() + 5000;
          setResumeAuthLock(false, "resume-finish");
          setResumeGateActive(false);
        }
      });
    })();

    return () => {
      listener?.remove?.();
    };
  }, [
    captureForcedLogout,
    clearLogoutTimeout,
    dispatch,
    log,
    sendResumeTelemetry,
    setResumeAuthLock,
  ]);

  // Hydrate biometric flag from secure storage on mount
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    void (async () => {
      try {
        await dispatch(hydrateBiometricsLoggedIn());
      } finally {
        setBiometricHydrationDone(true);
        if (!hydrationLoggedRef.current) {
          hydrationLoggedRef.current = true;
          log("biometric hydration finished");
        }
      }
    })();
  }, [dispatch, log]);

  // Set the difference between the server and the client in milliseconds
  useEffect(() => {
    if (!executedAt) return;

    const computeDiff = () =>
      dayjs().utc().diff(dayjs.utc(executedAt), "millisecond");

    // On native, prefer secure storage (biometric credential) to avoid localStorage
    if (Capacitor.isNativePlatform()) {
      (async () => {
        try {
          const credential = await fetchStoredBiometricCredential(
            "protected-route:server-diff",
          );
          const secureDiff =
            credential && typeof credential.serverDiffMs === "number"
              ? credential.serverDiffMs
              : undefined;

          if (typeof secureDiff === "number" && !Number.isNaN(secureDiff)) {
            setServerDiff(secureDiff);
            serverDiffExecuteAtRef.current = executedAt;
            log("serverDiff: loaded from secure storage", secureDiff);
            return;
          }
        } catch {
          // ignore and fallback to runtime compute
        }

        setServerDiff(computeDiff());
        serverDiffExecuteAtRef.current = executedAt;
        log("serverDiff: computed (native)");
      })();
      return;
    }

    // Web: migrate legacy per-tab value from sessionStorage to shared localStorage
    const legacySessionDiff = sessionStorage.getItem("serverDiff");
    if (legacySessionDiff) {
      const legacyNumber = Number(legacySessionDiff);
      sessionStorage.removeItem("serverDiff");
      if (!Number.isNaN(legacyNumber)) {
        const payload = { diff: legacyNumber, executeAt: executedAt };
        localStorage.setItem("serverDiff", JSON.stringify(payload));
        setServerDiff(legacyNumber);
        serverDiffExecuteAtRef.current = executedAt;
        return;
      }
    }

    // Prefer shared value if it matches the current token's execute_at
    const storedDiff = localStorage.getItem("serverDiff");

    try {
      if (storedDiff) {
        const parsed = JSON.parse(storedDiff);
        if (
          typeof parsed?.diff === "number" &&
          parsed?.executeAt === executedAt
        ) {
          setServerDiff(parsed.diff);
          serverDiffExecuteAtRef.current = executedAt;
          log("serverDiff: loaded from localStorage", parsed.diff);
          return;
        }
      }
    } catch {
      // Malformed localStorage value; ignore and recalculate below
    }

    // No reusable value: calculate diff from current execute_at and persist it (web only)
    const diff = computeDiff();
    const payload = { diff, executeAt: executedAt };

    localStorage.setItem("serverDiff", JSON.stringify(payload));
    setServerDiff(diff);
    serverDiffExecuteAtRef.current = executedAt;
    log("serverDiff: computed (web)", diff);
  }, [executedAt, log]);

  // Refresh token if the user is active + check expiration on visibility
  useEffect(() => {
    if (!executedAt || serverDiff === undefined || !isInForeground) return;

    const handleActivity = () => {
      const { expiresAt: liveExpiresAt, serverDiff: liveServerDiff } =
        liveAuthRef.current;

      const {
        biometricsInProgress: liveBiometricsInProgress,
        biometricsOptInPage: liveBiometricsOptInPage,
        resumeAuthLock,
        isInForeground: liveIsInForeground,
      } = liveTimeoutGuardsRef.current;

      if (
        shouldBlockExpiryHandlers({
          isInForeground: liveIsInForeground,
          biometricsInProgress: liveBiometricsInProgress,
          biometricsOptInPage: liveBiometricsOptInPage,
          resumeAuthLock,
        })
      ) {
        // Skip expiry checks while biometrics/resume/opt-in is in progress
        return;
      }

      // Skip expiration checks briefly after biometric reauth to allow new tokens to settle
      if (Date.now() < skipExpiryUntilRef.current) {
        return;
      }

      const expiresAt = liveExpiresAt;
      const currentTime = dayjs().utc();

      // If we don't have fresh auth data yet, skip activity handling
      if (!expiresAt || liveServerDiff === undefined) {
        return;
      }

      // Fix the system dates subtracting the difference between the server date to account for wrong system dates and wrong timezones
      const lastActivityWithDiff = lastActivityTimeRef.current
        .subtract(liveServerDiff, "millisecond");
      const currentTimeWithDiff = currentTime.subtract(liveServerDiff, "millisecond");

      const timeSinceLastActivity =
        currentTimeWithDiff.diff(lastActivityWithDiff);

      // If is expired, logout
      if (expiresAt && dayjs(expiresAt).utc().isBefore(currentTimeWithDiff)) {
        // On native, the resume listener handles reauth and may restore the session.
        // Don't force logout here — let the resume flow decide.
        if (Capacitor.isNativePlatform() && liveBiometricsLoggedInRef.current) {
          log("activity: token expired but deferring to resume flow (native + biometrics)", {
            expiresAt,
            currentTimeWithDiff: currentTimeWithDiff.toISOString(),
          });
          return;
        }
        log("activity: logout due to expired token", {
          expiresAt,
          currentTimeWithDiff: currentTimeWithDiff.toISOString(),
        });
        captureForcedLogout("activity_expired_token", {
          expiresAt,
          currentTimeWithDiff: currentTimeWithDiff.toISOString(),
        });
        return;
      }

      // If not, and the time since the last activity is greater than the debounce time, refresh the token
      if (timeSinceLastActivity > DEBOUNCE_TIME) {
        log("activity: refresh token", { timeSinceLastActivity });
        dispatch(refreshToken());
        lastActivityTimeRef.current = currentTime;
      }
    };

    const handleVisibilityChange = () => {
      const { expiresAt: liveExpiresAt, serverDiff: liveServerDiff } =
        liveAuthRef.current;

      const {
        biometricsInProgress: liveBiometricsInProgress,
        biometricsOptInPage: liveBiometricsOptInPage,
        resumeAuthLock,
        isInForeground: liveIsInForeground,
      } = liveTimeoutGuardsRef.current;

      if (
        shouldBlockExpiryHandlers({
          isInForeground: liveIsInForeground,
          biometricsInProgress: liveBiometricsInProgress,
          biometricsOptInPage: liveBiometricsOptInPage,
          resumeAuthLock,
        })
      ) {
        // Skip expiry checks while biometrics/resume/opt-in is in progress
        return;
      }

      // Only check when page becomes visible
      if (document.visibilityState === "visible") {
        // On native, visibilitychange fires BEFORE Capacitor resume listener.
        // Defer expiry checks to the resume flow which handles reauth properly.
        if (Capacitor.isNativePlatform()) {
          log("visibility: deferring to resume listener on native");
          return;
        }

        if (Date.now() < skipExpiryUntilRef.current) {
          return;
        }

        if (!liveExpiresAt || liveServerDiff === undefined) {
          return;
        }

        const currentTime = dayjs().utc();
        const currentTimeWithDiff = currentTime
          .subtract(liveServerDiff, "millisecond");

        const msToExpire = dayjs(liveExpiresAt).utc().diff(currentTimeWithDiff);
        log("visibility: token expiration check", {
          expiresAt: liveExpiresAt,
          currentTimeWithDiff: currentTimeWithDiff.toISOString(),
          msToExpire,
          secondsToExpire: Math.round(msToExpire / 1000),
          minutesToExpire: Math.round(msToExpire / 60000),
        });

        // ONLY check if token expired - DO NOT refresh automatically
        if (
          liveExpiresAt &&
          dayjs(liveExpiresAt).utc().isBefore(currentTimeWithDiff)
        ) {
          log("visibility: logout due to expired token", {
            liveExpiresAt,
            currentTimeWithDiff: currentTimeWithDiff.toISOString(),
          });
          captureForcedLogout("visibility_expired_token", {
            expiresAt: liveExpiresAt,
            currentTimeWithDiff: currentTimeWithDiff.toISOString(),
          });
          return;
        }

        // Token is still valid - do nothing
        // handleActivity will refresh when user actually interacts
      }
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (Capacitor.isNativePlatform()) {
      window.addEventListener("touchstart", handleActivity);
      window.addEventListener("touchmove", handleActivity);
      window.addEventListener("pointerdown", handleActivity);
    }

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      
      if (Capacitor.isNativePlatform()) {
        window.removeEventListener("touchstart", handleActivity);
        window.removeEventListener("touchmove", handleActivity);
        window.removeEventListener("pointerdown", handleActivity);
      }
    };
  }, [
    captureForcedLogout,
    dispatch,
    auth?.expires_at,
    serverDiff,
    executedAt,
    biometricsInProgress,
    isInForeground,
    log,
  ]);

  // If expire_at date is reached, logout
  // Don't logout if we have tokens in URL, app in background, or biometrics in progress
  useEffect(() => {
    clearLogoutTimeout();

    if (
      !isInForeground ||
      biometricsInProgress ||
      biometricsOptInPage ||
      resumeGateActive ||
      resumeAuthLockRef.current
    ) {
      // Don't arm logout timers while biometric flows are active or app is backgrounded
      log("logoutTimer: skipped (inactive or biometric flow)", {
        isInForeground,
        biometricsInProgress,
        biometricsOptInPage,
        resumeGateActive,
        resumeAuthLock: resumeAuthLockRef.current,
      });
      return;
    }

    const expiresAt = auth?.expires_at;

    // Don't arm timer if serverDiff hasn't been recalculated for the current execute_at yet
    if (
      !expiresAt ||
      !executedAt ||
      serverDiff === undefined ||
      hasUrlTokens ||
      serverDiffExecuteAtRef.current !== executedAt
    )
      return;

    const currentTime = dayjs().utc();

    const currentTimeWithDiff = currentTime.subtract(serverDiff, "millisecond");

    const milisecondsToExpire = dayjs(expiresAt)
      .utc()
      .diff(currentTimeWithDiff);

    if (milisecondsToExpire > MAX_LOGOUT_TIMEOUT_MS) {
      log("logoutTimer: skipped (timeout > max)", { milisecondsToExpire });
      return;
    }

    if (milisecondsToExpire <= 0) {
      if (biometrics?.loggedIn) {
        log("logoutTimer: skipped (already expired but biometrics logged in)");
        return;
      }
      // Token already expired and no biometric recovery — logout immediately
      // Don't arm a setTimeout with negative/zero delay
      log("logoutTimer: immediate logout (token already expired)", {
        milisecondsToExpire,
      });
      captureForcedLogout("logout_timer_expired", {
        milisecondsToExpire,
      });
      return;
    }

    logoutTimeoutRef.current = window.setTimeout(() => {
      const {
        biometricsInProgress: liveBiometricsInProgress,
        isInForeground: liveIsInForeground,
        biometricsOptInPage: liveBiometricsOptInPage,
        resumeAuthLock,
      } = liveTimeoutGuardsRef.current;

      // Skip logout if biometric/resume flow is running or app went to background
      if (
        shouldBlockExpiryHandlers({
          isInForeground: liveIsInForeground,
          biometricsInProgress: liveBiometricsInProgress,
          biometricsOptInPage: liveBiometricsOptInPage,
          resumeAuthLock,
        })
      ) {
        log("logoutTimer: aborted (biometric/resume flow active)", {
          liveBiometricsInProgress,
          liveBiometricsOptInPage,
          liveIsInForeground,
          resumeAuthLock,
        });
        return;
      }
      // Safety net: skip if expiry checks are paused (e.g. reauth just completed)
      if (Date.now() < skipExpiryUntilRef.current) {
        log("logoutTimer: aborted (skipExpiryUntil active)");
        return;
      }
      log("logoutTimer: firing logout", { milisecondsToExpire });
      captureForcedLogout("logout_timer_expired", {
        milisecondsToExpire,
      });
    }, milisecondsToExpire);
    log("logoutTimer: armed", {
      expiresAt,
      milisecondsToExpire,
      secondsToExpire: Math.round(milisecondsToExpire / 1000),
      minutesToExpire: Math.round(milisecondsToExpire / 60000),
    });

    return () => {
      clearLogoutTimeout();
    };
  }, [
    auth?.expires_at,
    serverDiff,
    isInForeground,
    biometricsInProgress,
    biometricsOptInPage,
    resumeGateActive,
    executedAt,
    hasUrlTokens,
    captureForcedLogout,
    clearLogoutTimeout,
    dispatch,
    log,
  ]);

  // Make one refreshToken call at app start to check if the token is still valid
  const initialRefreshCheckDoneRef = useRef(false);

  useEffect(() => {
    if (!auth?.refresh_token || initialRefreshCheckDoneRef.current) return;

    initialRefreshCheckDoneRef.current = true;
    log("initial refreshToken check — DISPATCHING NOW");
    dispatch(refreshToken())
      .unwrap()
      .then(() => {
        log("initial refreshToken: resolved successfully");
      })
      .catch((err) => {
        log("initial refreshToken: rejected", {
          errorMessage: typeof err === "string" ? err : (err as Error)?.message,
        });
      });
  }, [auth?.refresh_token, dispatch, log]);

  // Save last path for redirect after login
  useEffect(() => {
    if (!user && pathname !== "/" && !isLogout) {
      localStorage.setItem("lastPath", pathname);
    } else {
      localStorage.removeItem("lastPath");
      localStorage.removeItem("logout");
    }
  }, [user, pathname, isLogout]);

  // Process tokens from URL if present
  useEffect(() => {
    if (
      hasUrlTokens &&
      accessToken &&
      expiresIn &&
      expiresAt &&
      executeAt &&
      !auth?.access_token
    ) {
      if (!isInForeground || biometricsInProgress) return;
      log("tokens: setting auth from URL");
      dispatch(
        setAuthFromUrl({
          accessToken,
          refreshToken: refreshTokenParam,
          expiresIn,
          expiresAt,
          executeAt,
          state: stateParam,
        }),
      );
    }
  }, [
    hasUrlTokens,
    accessToken,
    refreshTokenParam,
    expiresIn,
    expiresAt,
    executeAt,
    auth?.access_token,
    dispatch,
    log,
  ]);

  // Remove tokens from URL only after auth token is confirmed in store
  useEffect(() => {
    if (!isInForeground || biometricsInProgress) return;

    if (auth?.access_token && hasUrlTokens) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("accessToken");
      nextParams.delete("refreshToken");
      nextParams.delete("expiresIn");
      nextParams.delete("expiresAt");
      nextParams.delete("executeAt");
      nextParams.delete("state");
      setSearchParams(nextParams, { replace: true });
      log("tokens: removed from URL after auth confirmed");
    }
  }, [
    auth?.access_token,
    hasUrlTokens,
    searchParams,
    setSearchParams,
    isInForeground,
    biometricsInProgress,
    log,
  ]);

  // If auth token exists and user is not fetched, fetch user
  useEffect(() => {
    if (!isInForeground || biometricsInProgress) return;

    if (auth?.access_token && !user) {
      log("user: fetching profile");
      dispatch(getUserProfile());
    }
  }, [
    dispatch,
    auth?.access_token,
    user,
    isInForeground,
    biometricsInProgress,
    log,
  ]);

  // Native OTA/app reload: if auth is missing but we still have a valid biometric credential, try to restore session before redirect
  const bootstrapAttemptedRef = useRef(false);
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!biometricHydrationDone) return;
    if (bootstrapAttemptedRef.current) return;
    if (auth?.access_token) return;
    if (!isInForeground) return;
    if (resumeBiometricInProgressRef.current) return;
    if (biometricsInProgress) return;

    bootstrapAttemptedRef.current = true;

    void (async () => {
      const secureSession = await fetchStoredBiometricCredential(
        "protected-route:bootstrap",
      );
      const validation = validateBiometricCredential(secureSession);

      if (!validation.valid) {
        log("bootstrap: skipped, no valid biometric credential", {
          expired: validation.expired,
        });
        return;
      }

      // Block activity/visibility/logoutTimer handlers while bootstrap reauth is in progress
      setResumeAuthLock(true, "bootstrap-start");
      skipExpiryUntilRef.current = Infinity;
      clearLogoutTimeout();

      try {
        log("bootstrap: attempting biometric reauth to restore auth");

        const credential = validation.credential as ValidBiometricCredential;
        const reauthResult = await performBiometricReauth({
          dispatch,
          credential,
          userEmail: secureSession?.username ?? user?.email ?? null,
          recomputeServerDiff: true,
        });

        if (!reauthResult.success) {
          log("bootstrap: biometric reauth failed", reauthResult);
          sendResumeTelemetry("biometric_reauth_failed", {
            path: "bootstrap",
            step: reauthResult.step,
            error: reauthResult.error,
            errorCode: reauthResult.errorCode,
            nativeMessage: reauthResult.nativeMessage,
          });
          if (reauthResult.step === "prompt") {
            log("bootstrap: prompt canceled, leaving flow to LoginPage");
            return;
          }

          dispatch(removeBiometricData());
          captureForcedLogout("bootstrap_biometric_reauth_failed", {
            step: reauthResult.step,
          });
          return;
        }

        if (!user) {
          await dispatch(getUserProfile());
        }

        log("bootstrap: biometric reauth success");
        sendResumeTelemetry("biometric_reauth_success", {
          path: "bootstrap",
        });
      } finally {
        // Give 5s grace for new tokens to propagate before re-enabling expiry checks
        skipExpiryUntilRef.current = Date.now() + 5000;
        setResumeAuthLock(false, "bootstrap-finish");
      }
    })();
  }, [
    auth?.access_token,
    biometricHydrationDone,
    biometricsInProgress,
    captureForcedLogout,
    dispatch,
    isInForeground,
    log,
    sendResumeTelemetry,
    user,
  ]);

  useEffect(() => {
    const hold =
      !isInForeground ||
      resumeGateHold ||
      (biometricsInProgress && !biometricsOptInPage) ||
      !biometricHydrationDone;
    if (renderGateLoggedRef.current === hold) return;
    renderGateLoggedRef.current = hold;
    log("render gate", {
      hold,
      isInForeground,
      resumeGateHold,
      biometricsInProgress,
      biometricsOptInPage,
      biometricHydrationDone,
    });
  }, [
    resumeGateHold,
    biometricsInProgress,
    biometricsOptInPage,
    biometricHydrationDone,
    isInForeground,
    log,
  ]);

  // Per-input change logs (fire even when overall hold value does not change,
  // so we can see WHICH input is keeping the gate stuck during diagnostics).
  useEffect(() => {
    log("gate input: isInForeground changed", { isInForeground });
  }, [isInForeground, log]);

  useEffect(() => {
    log("gate input: biometricHydrationDone changed", { biometricHydrationDone });
  }, [biometricHydrationDone, log]);

  useEffect(() => {
    log("gate input: resumeGateHold changed", {
      resumeGateHold,
      resumeGateActive,
      resumedThisRender,
      wasPaused: wasPausedRef.current,
      wasInForeground: wasInForegroundRef.current,
    });
  }, [resumeGateHold, resumeGateActive, resumedThisRender, log]);

  useEffect(() => {
    log("gate input: biometricsInProgress changed", {
      biometricsInProgress,
      biometricsOptInPage,
      reduxInProgress: biometrics?.inProgress ?? false,
      resumeBiometricInProgress: resumeBiometricInProgressRef.current,
    });
  }, [biometricsInProgress, biometricsOptInPage, biometrics?.inProgress, log]);

  // Allow render during opt-in (optInPage) but still block while other biometric flows run
  const shouldHoldRender =
    !isInForeground ||
    resumeGateHold ||
    (biometricsInProgress && !biometricsOptInPage) ||
    !biometricHydrationDone;

  const renderState = shouldHoldRender
    ? "hold"
    : (hasUrlTokens || (auth?.access_token && !user)) && !error
      ? "waitingTokensOrUser"
      : !user
        ? "redirectLogin"
        : "renderChildren";

  useEffect(() => {
    if (renderStateRef.current === renderState) return;
    renderStateRef.current = renderState;
    log("render state", { renderState, error });
  }, [error, log, renderState]);

  if (shouldHoldRender) {
    return Capacitor.isNativePlatform() ? (
      <LoadingSpinner className={spinnerContainer} size="lg" />
    ) : null;
  }

  // Don't redirect if we have tokens in URL or have auth token but user is still loading
  // However, if there's an error fetching the user, redirect to login
  if ((hasUrlTokens || (auth?.access_token && !user)) && !error) {
    // This will prevent redirect to login while tokens are being processed or getUserProfile is in progress
    return null;
  }

  if (!user) {
    return <Navigate to={`${PATH_APP.login}${location.search}`} replace />;
  }

  return children;
};

export default ProtectedRoute;
